package com.daytimegaming.ourdailyfinances.presentation.auth

/**
 * Formats authentication error messages to display clean, single-line messages to the user.
 * Avoids displaying raw JSON error objects from auth APIs or Firebase SDK exceptions.
 */
fun formatAuthErrorMessage(
    rawMessage: String?,
    defaultMessage: String = "Invalid Username or Password"
): String {
    if (rawMessage.isNullOrBlank()) return defaultMessage

    val upper = rawMessage.uppercase()

    // 1. Check for invalid credential error patterns
    val isInvalidCredentials = upper.contains("INVALID_LOGIN_CREDENTIALS") ||
            upper.contains("FIREBASEAUTHINVALIDCREDENTIALSEXCEPTION") ||
            upper.contains("FIREBASEAUTHINVALIDUSEREXCEPTION") ||
            upper.contains("AUTH CREDENTIAL IS INCORRECT") ||
            upper.contains("INVALID_CREDENTIALS") ||
            upper.contains("WRONG_PASSWORD") ||
            upper.contains("INVALID_PASSWORD") ||
            upper.contains("USER_NOT_FOUND") ||
            upper.contains("EMAIL_NOT_FOUND")

    if (isInvalidCredentials) {
        return "Invalid Username or Password"
    }

    // 2. Check for other specific Firebase error code tokens
    when {
        upper.contains("INVALID_EMAIL") || upper.contains("FIREBASEAUTHINVALIDFORMATEXCEPTION") ->
            return "Invalid email address"
        upper.contains("EMAIL_EXISTS") || upper.contains("EMAIL_ALREADY_IN_USE") || upper.contains("FIREBASEAUTHUSERCOLLISIONEXCEPTION") ->
            return "An account with this email already exists"
        upper.contains("WEAK_PASSWORD") || upper.contains("FIREBASEAUTHWEAKPASSWORDEXCEPTION") ->
            return "Password should be at least 6 characters"
        upper.contains("USER_DISABLED") ->
            return "This user account has been disabled"
        upper.contains("TOO_MANY_ATTEMPTS_TRY_LATER") ->
            return "Too many failed attempts. Please try again later"
        upper.contains("OPERATION_NOT_ALLOWED") ->
            return "Operation not allowed"
        upper.contains("NETWORK_REQUEST_FAILED") ->
            return "Network error. Please check your connection"
    }

    // 3. Try extracting message from JSON structure if rawMessage is a JSON object/string
    val jsonMessageRegex = """"message"\s*:\s*"([^"]+)"""".toRegex()
    val match = jsonMessageRegex.find(rawMessage)
    if (match != null) {
        val extracted = match.groupValues[1]
        val extractedUpper = extracted.uppercase()

        if (extractedUpper.contains("INVALID_LOGIN_CREDENTIALS") ||
            extractedUpper.contains("INVALID_CREDENTIALS") ||
            extractedUpper.contains("WRONG_PASSWORD") ||
            extractedUpper.contains("INVALID_PASSWORD") ||
            extractedUpper.contains("USER_NOT_FOUND") ||
            extractedUpper.contains("EMAIL_NOT_FOUND")
        ) {
            return "Invalid Username or Password"
        }

        if (extractedUpper.contains("INVALID_EMAIL")) return "Invalid email address"
        if (extractedUpper.contains("EMAIL_EXISTS") || extractedUpper.contains("EMAIL_ALREADY_IN_USE")) return "An account with this email already exists"
        if (extractedUpper.contains("WEAK_PASSWORD")) return "Password should be at least 6 characters"
        if (extractedUpper.contains("USER_DISABLED")) return "This user account has been disabled"
        if (extractedUpper.contains("TOO_MANY_ATTEMPTS_TRY_LATER")) return "Too many failed attempts. Please try again later"

        // If extracted is clean readable text (not JSON)
        if (!extracted.contains("{") && !extracted.contains("}") && !extracted.contains("[")) {
            return extracted
        }
    }

    // 4. If string starts with JSON markers or contains raw JSON braces, fallback to default single line message
    val trimmed = rawMessage.trim()
    if (trimmed.startsWith("{") || trimmed.startsWith("[") || trimmed.contains("{\"") || trimmed.contains("{\n")) {
        return defaultMessage
    }

    return rawMessage
}
